import { Box, Card, CircularProgress, Drawer, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { CardContagem, Container, HeaderContainer } from "./styles";
import { FiltrosListagemPedidos } from "./containers/FiltrosListagemPedidos";
import { useFetchPedidos } from "../../hooks/useFetchPedidos";
import { useEffect, useRef, useState } from "react";
import { IPedido } from "../../interface/IPedido";
import { usePedidos } from "../../hooks/usePedidos";
import { useSearchParams } from "react-router-dom";
import { StatusPedido, StatusPedidoDescricao } from "../../enum/StatusPedido";
import { useMarketplaces } from "../../../marketplace/hooks/useMarketplaces";
import { useFetchMarketplaces } from "../../../marketplace/hooks/useFetchMarketplaces";
import { TabelaPedidos } from "./containers/TabelaPedidos";
import { ChevronLeft, Close, ReceiptLong } from "@mui/icons-material";
import { PedidoDetalhado } from "../../containers/PedidoDetalhado";
import { useTamanhoTela } from "../../../core/hooks/useTamanhoTela";
import { FerramentasPedidos } from "./containers/FerramentasPedidos";
import { useWebSocketPedidosContext } from "../../contexts/WebSocketPedidosContext";
import { MiniKDSDrawer } from "./containers/MiniKDS/MiniKDSDrawer";
import { IFiltrosPedidos } from "../../interface/IFiltrosPedidos";
import { useMesasQRCode } from "../../hooks/useMesasQRCode";
import { ISessaoMesa, StatusSessaoMesa } from "../../interface/ISessaoMesa";

type TabListagemPedidos = 'Em aberto' | 'Concluído' | 'Cancelado';

const STATUS_POR_TAB: Record<TabListagemPedidos, StatusPedidoDescricao[]> = {
    'Em aberto': [
        StatusPedidoDescricao["Em preparo"],
        StatusPedidoDescricao["Em andamento"],
        StatusPedidoDescricao["Em transporte"],
        StatusPedidoDescricao["Aguardando confirmação"],
        StatusPedidoDescricao["Aguardando pagamento"],
        StatusPedidoDescricao["Pronto para retirada"],
        StatusPedidoDescricao["Em negociação"],
    ],
    'Concluído': [StatusPedidoDescricao["Concluído"]],
    'Cancelado': [StatusPedidoDescricao["Cancelado"]],
};

export const ListagemPedidos = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [pedidos, setPedidos] = useState<IPedido[]>([]);
    const [loading, setLoading] = useState(false);
    const [pedidosSelecionados, setPedidosSelecionados] = useState<IPedido[]>([]);
    const [uuidPedidoDetalhes, setUuidPedidoDetalhes] = useState<string | undefined>(undefined);
    const [tab, setTab] = useState<TabListagemPedidos>('Em aberto');
    const [loadingExportacao, setLoadingExportacao] = useState(false);
    const { listarPedidos, exportarPedidos } = usePedidos();
    const { marketplaces, loading: loadingMarketplaces } = useFetchMarketplaces();
    const { ultimoPedidoAtualizado, registrarCallbackAtualizacao, removerCallbackAtualizacao } = useWebSocketPedidosContext();
    const ultimaInteracao = useRef<Date | null>(null);

    // Mesas do QR Code com a conta pedida, por pedido. Pedir a conta não altera o pedido,
    // então a listagem acompanha as sessões pelos eventos de mesa do WebSocket.
    const { listarSessoes } = useMesasQRCode();
    const [contasSolicitadas, setContasSolicitadas] = useState<Map<string, ISessaoMesa>>(new Map());

    const filtrosDaBusca = (): IFiltrosPedidos => {
        const tipoFiltroPorData = searchParams.get('tipoFiltroPorData') ?? 'emissao';
        return {
            dataInicio: tipoFiltroPorData === 'emissao' ? searchParams.get('dataInicio') : null,
            dataFim: tipoFiltroPorData === 'emissao' ? searchParams.get('dataFim') : null,
            numeroPedido: searchParams.get('numeroPedido'),
            tipos: searchParams.get('tipos'),
            marketplace: searchParams.get('marketplace'),
            agendadoParaInicio: tipoFiltroPorData === 'agendamento' ? searchParams.get('dataInicio') : null,
            agendadoParaFim: tipoFiltroPorData === 'agendamento' ? searchParams.get('dataFim') : null,
        };
    };

    /** Exporta as três abas de uma vez, com os filtros aplicados na tela. */
    const handleExportar = async () => {
        if (!searchParams.get('dataInicio') || !searchParams.get('dataFim')) return;

        setLoadingExportacao(true);
        await exportarPedidos({
            ...filtrosDaBusca(),
            status: Object.values(STATUS_POR_TAB).flat().join(','),
        });
        setLoadingExportacao(false);
    };

    const handleListarPedidos = async (mostrarLoading = true) => {
        if (!searchParams.get('dataInicio') || !searchParams.get('dataFim')) return;

        if (mostrarLoading) setLoading(true);

        const pedidosListados = await listarPedidos({
            ...filtrosDaBusca(),
            status: STATUS_POR_TAB[tab].join(','),
        });

        if (pedidosListados) {
            setPedidos(pedidosListados);
        }

        setPedidosSelecionados([]);
        if (mostrarLoading) setLoading(false);
    };

    const selecionarPedido = (pedido: IPedido) => {
        const pedidoJaSelecionadoIndex = pedidosSelecionados?.findIndex(pedidoSelecionado => pedidoSelecionado.uuid === pedido.uuid);
    
        if (pedidoJaSelecionadoIndex === -1) {
            setPedidosSelecionados(pedidosSelecionados => [...pedidosSelecionados, pedido]);
        } else {
            setPedidosSelecionados(pedidosSelecionados => pedidosSelecionados.filter(p => p.uuid !== pedido.uuid));
        }

        ultimaInteracao.current = new Date();
    };
    

    const handleTab = (event: React.SyntheticEvent, tab: TabListagemPedidos) => {
        setTab(tab);
    };

    useEffect(() => {
        const intervalo = setInterval(() => {
            const agora = new Date();
            const ultimaInteracaoTempo = ultimaInteracao.current
                ? (agora.getTime() - ultimaInteracao.current.getTime()) / 1000
                : Infinity;

            if (ultimaInteracaoTempo > 120) {
                setPedidosSelecionados([]);
                ultimaInteracao.current = null;
            }

            if (ultimaInteracaoTempo >= 0) {
                handleListarPedidos(false);
            }
        }, 30000);

        return () => clearInterval(intervalo);
    }, [searchParams, tab]);

    useEffect(() => {
        handleListarPedidos(true);
    }, [searchParams, tab]);

    useEffect(() => {
        if(!pedidosSelecionados?.length) 
        handleListarPedidos(false);
    }, [ultimoPedidoAtualizado])

    useEffect(() => {
        const carregarContasSolicitadas = async () => {
            const sessoes = await listarSessoes();
            setContasSolicitadas(new Map(sessoes
                .filter(sessao => sessao.status === StatusSessaoMesa.CONTA_SOLICITADA)
                .map(sessao => [sessao.pedidoUuid, sessao])));
        };

        carregarContasSolicitadas();
        registrarCallbackAtualizacao?.(carregarContasSolicitadas);
        return () => removerCallbackAtualizacao?.(carregarContasSolicitadas);
    }, [ultimoPedidoAtualizado]);

    function tabProps(uuid: number) {
        return {
            id: `simple-tab-${uuid}`,
            'aria-controls': `simple-tabpanel-${uuid}`,
        };
    }

    const abrirDetalhes = (uuid: string) => setUuidPedidoDetalhes(uuid);
    const fecharDetalhes = () => setUuidPedidoDetalhes(undefined);
    const { modeloTela } = useTamanhoTela();

    const [miniKDSTemItens, setMiniKDSTemItens] = useState(false);
    const pedidoSelecionadoUuid = pedidosSelecionados[0]?.uuid;
    useEffect(() => { setMiniKDSTemItens(false); }, [pedidoSelecionadoUuid]);

    const miniKDSAberto = pedidosSelecionados.length === 1 && miniKDSTemItens;
    const MINI_KDS_WIDTH = 440;

    return (
        <>
        <Container sx={{
            transition: 'margin-right 225ms ease',
            marginRight: miniKDSAberto && modeloTela === 'desktop' ? `${MINI_KDS_WIDTH}px` : 0,
        }}>
            {uuidPedidoDetalhes && (
                <Drawer
                    anchor="right"
                    open={true}
                    onClose={fecharDetalhes}
                    sx={{ zIndex: 9999 }}
                >
                    <Box sx={{ 
                        width: modeloTela == "desktop" ? '60vw' : '100vw', 
                        padding: '1rem',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end" 
                    }}>
                        <IconButton onClick={fecharDetalhes}>
                            <Close />
                        </IconButton>
                        <PedidoDetalhado
                            uuid={uuidPedidoDetalhes}
                            onStatusAtualizado={() => handleListarPedidos(false)}
                            dialogZIndex={10000}
                        />
                    </Box>
                </Drawer>
            )}
            <HeaderContainer>
                <Typography variant={"h5"}>
                    Pedidos
                </Typography>
                <Card variant="outlined">
                    <CardContagem>
                        <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.5rem"
                        }}>
                            <Typography fontSize={12} color={"GrayText"}>
                                {tab}
                            </Typography>
                            {loading
                                ? <CircularProgress color="primary" size={10} />
                                : <Typography>{pedidos?.length}</Typography>
                            }
                        </Box>
                        <ReceiptLong color="primary" sx={{ fontSize: 30 }} />
                    </CardContagem>
                </Card>
            </HeaderContainer>
            <Tabs onChange={handleTab} value={tab} sx={{ mt: "1rem", borderBottom: 1, borderColor: 'divider', width: "100%" }}>
                <Tab 
                    label="Em aberto" 
                    value={'Em aberto'} 
                    {...tabProps(StatusPedido["Em preparo"])} 
                />
                <Tab 
                    label="Concluídos" 
                    value={'Concluído'} 
                    {...tabProps(StatusPedido["Concluído"])} 
                />
                <Tab 
                    label="Cancelados" 
                    value={'Cancelado'} 
                    {...tabProps(StatusPedido["Cancelado"])} 
                />
            </Tabs>
            <FiltrosListagemPedidos
                marketplaces={marketplaces}
                loadingMarketplaces={loadingMarketplaces}
                onExportar={handleExportar}
                loadingExportacao={loadingExportacao}
            />
            <TabelaPedidos
                pedidos={pedidos}
                loading={loading}
                loadingMarketplaces={loadingMarketplaces}
                marketplaces={marketplaces}
                abrirDetalhes={abrirDetalhes}
                pedidosSelecionados={pedidosSelecionados}
                selecionarPedido={selecionarPedido}
                contasSolicitadas={contasSolicitadas}
            />
            <FerramentasPedidos
                pedidosSelecionados={pedidosSelecionados}
                atualizarListagem={handleListarPedidos}
                desselecionarPedido={selecionarPedido}
            />
        </Container>
        <MiniKDSDrawer
            open={miniKDSAberto}
            pedido={pedidosSelecionados.length === 1 ? pedidosSelecionados[0] : null}
            onClose={() => setPedidosSelecionados([])}
            onTemItens={() => setMiniKDSTemItens(true)}
        />
        </>
    );
}